import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  getFacturadorEmpresas,
  activarFacturador,
  configurarFacturador,
  configurarLimitesFacturador,
  getFacturadorDocumentos,
  getFacturadorAuditoria,
  getFacturadorAdicionales,
} from '../services/adminApi';
import { NOMBRES_TIPO, pesos, fechaCorta, fechaChile, hoyChile, mensajeError, EstadoBadge, nombreDocumento, FiltroPeriodo, nombreMes } from './Facturador/comun';
import '../styles/pages/Facturador.css';
import '../styles/pages/AdminFacturadorPage.css';

const TIPOS_FASE_1 = [33, 34, 46, 56, 61];

const ACCIONES = {
  documento_emitido: 'Emitido',
  documento_enviado: 'Enviado al SII',
  envio_rechazado: 'El SII rechazó el envío',
  envio_no_recibido: 'El SII no recibió el envío',
  envio_pendiente: 'Sin respuesta del SII (se reintenta)',
  estado_sii: 'Respuesta del SII',
  xml_entregado: 'XML entregado al receptor',
  aviso_enviado: 'Aviso enviado',
  aviso_no_enviado: 'Aviso no enviado',
};

const ORIGENES = { panel: 'Panel', renata: 'Renata', admin: 'EFE', sistema: 'Sistema' };

const fechaHora = (valor) =>
  new Date(valor).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'medium', timeZone: 'America/Santiago' });

// ─── Modal configurar (ambiente, tipos y resolución) ──────────────────────────

function ConfigurarModal({ empresa, onGuardado, onCerrar }) {
  const [ambiente, setAmbiente] = useState(empresa.ambiente || 'certificacion');
  const [tipos, setTipos] = useState((empresa.tipos_habilitados || []).map(Number));
  const [numero, setNumero] = useState(empresa.resolucion_numero ?? '');
  const [fecha, setFecha] = useState(empresa.resolucion_fecha || '');
  const [guardando, setGuardando] = useState(false);

  const alternar = (tipo) => setTipos((t) => (t.includes(tipo) ? t.filter((x) => x !== tipo) : [...t, tipo]));

  const guardar = async () => {
    setGuardando(true);
    try {
      await configurarFacturador(empresa.id_company, {
        ambiente,
        tipos_habilitados: tipos,
        resolucion_numero: numero === '' ? null : Number(numero),
        resolucion_fecha: fecha || null,
      });
      onGuardado();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudo guardar') });
      setGuardando(false);
    }
  };

  return (
    <div className="fe-modal-overlay" onClick={onCerrar}>
      <div className="fe-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="fe-modal-title">Configurar · {empresa.empresa}</h3>
        <div className="fe-form-group">
          <label>Ambiente del SII</label>
          <select value={ambiente} onChange={(e) => setAmbiente(e.target.value)}>
            <option value="certificacion">Certificación (maullin)</option>
            <option value="produccion">Producción (palena)</option>
          </select>
        </div>
        <div className="fe-form-group" style={{ marginTop: 12 }}>
          <label>Documentos habilitados</label>
          <div className="fe-opciones">
            {TIPOS_FASE_1.map((tipo) => (
              <label key={tipo} className="fe-checkbox">
                <input type="checkbox" checked={tipos.includes(tipo)} onChange={() => alternar(tipo)} /> {tipo} · {NOMBRES_TIPO[tipo]}
              </label>
            ))}
          </div>
        </div>
        <div className="fe-form-grid" style={{ marginTop: 12 }}>
          <div className="fe-form-group">
            <label>N° de resolución</label>
            <input type="number" min="0" value={numero} onChange={(e) => setNumero(e.target.value)} />
          </div>
          <div className="fe-form-group">
            <label>Fecha de resolución</label>
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
        </div>
        <p className="fe-hint">En certificación el número de resolución es 0 y la fecha es la de la autorización del ambiente de pruebas.</p>
        <div className="fe-form-actions">
          <button className="fe-btn fe-btn--success" disabled={guardando} onClick={guardar}>{guardando ? 'Guardando...' : 'Guardar'}</button>
          <button className="fe-btn fe-btn--secondary" onClick={onCerrar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal límites ────────────────────────────────────────────────────────────

function LimitesModal({ empresa, onGuardado, onCerrar }) {
  const [cupo, setCupo] = useState(empresa.cupo_mensual ?? 0);
  const [tope, setTope] = useState(empresa.tope_mensual ?? '');
  const [valor, setValor] = useState(empresa.valor_adicional ?? '');
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setGuardando(true);
    try {
      await configurarLimitesFacturador(empresa.id_company, {
        cupo_mensual: Number(cupo),
        tope_mensual: tope === '' ? null : Number(tope),
        valor_adicional: valor === '' ? null : Number(valor),
      });
      onGuardado();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudieron guardar los límites') });
      setGuardando(false);
    }
  };

  return (
    <div className="fe-modal-overlay" onClick={onCerrar}>
      <div className="fe-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="fe-modal-title">Límites · {empresa.empresa}</h3>
        <div className="fe-form-grid">
          <div className="fe-form-group">
            <label>Cupo mensual</label>
            <input type="number" min="0" value={cupo} onChange={(e) => setCupo(e.target.value)} />
          </div>
          <div className="fe-form-group">
            <label>Tope mensual <span className="fe-muted">(vacío = sin tope)</span></label>
            <input type="number" min="0" value={tope} onChange={(e) => setTope(e.target.value)} />
          </div>
          <div className="fe-form-group">
            <label>Valor por adicional</label>
            <input type="number" min="0" value={valor} onChange={(e) => setValor(e.target.value)} />
          </div>
        </div>
        <p className="fe-hint">Sobre el cupo se sigue emitiendo y cada documento es adicional. Con el tope alcanzado no se emite hasta ampliarlo.</p>
        <div className="fe-form-actions">
          <button className="fe-btn fe-btn--success" disabled={guardando} onClick={guardar}>{guardando ? 'Guardando...' : 'Guardar'}</button>
          <button className="fe-btn fe-btn--secondary" onClick={onCerrar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal documentos y auditoría ─────────────────────────────────────────────

function Auditoria({ idDocumento, onVolver }) {
  const [datos, setDatos] = useState(null);

  useEffect(() => {
    getFacturadorAuditoria(idDocumento)
      .then(setDatos)
      .catch((err) => Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudo cargar la auditoría') }));
  }, [idDocumento]);

  if (!datos) return <p className="fe-loading">Cargando auditoría...</p>;
  const { documento, eventos } = datos;
  const emision = eventos.find((e) => e.accion === 'documento_emitido');

  return (
    <>
      <button className="fe-btn fe-btn--link" onClick={onVolver}>← Volver a la lista</button>
      <h4 className="fe-panel-title" style={{ marginTop: 10 }}>
        {nombreDocumento(documento)} <EstadoBadge estado={documento.estado} />
      </h4>
      <dl className="fe-datos">
        <dt>Receptor</dt><dd>{documento.razon_social_receptor} · {documento.rut_receptor}</dd>
        <dt>Total</dt><dd>{pesos(documento.monto_total)}</dd>
        <dt>Origen</dt><dd>{ORIGENES[documento.origen] || documento.origen}{documento.id_peticion ? ` · petición ${documento.id_peticion}` : ''}</dd>
        {documento.track_id && (<><dt>Track ID</dt><dd>{documento.track_id}</dd></>)}
        {documento.glosa_estado && (<><dt>Glosa del SII</dt><dd>{documento.glosa_estado}</dd></>)}
        <dt>Archivos</dt>
        <dd>
          {documento.pdf_url && <a href={documento.pdf_url} target="_blank" rel="noreferrer">PDF</a>}
          {' · '}
          {documento.xml_url && <a href={documento.xml_url} target="_blank" rel="noreferrer">XML</a>}
        </dd>
      </dl>
      {emision?.detalle?.mensaje_original && (
        <div className="fe-aviso fe-aviso--azul">
          <strong>Mensaje original:</strong> {emision.detalle.mensaje_original}
        </div>
      )}
      {emision?.detalle?.datos_interpretados && (
        <div className="fe-aviso fe-aviso--azul">
          <strong>Lo que entendió Renata:</strong>
          <pre className="afe-json">{JSON.stringify(emision.detalle.datos_interpretados, null, 2)}</pre>
        </div>
      )}
      <table className="fe-table">
        <thead><tr><th>Fecha</th><th>Acción</th><th>Origen</th><th>Detalle</th></tr></thead>
        <tbody>
          {eventos.map((e) => (
            <tr key={e.id}>
              <td style={{ whiteSpace: 'nowrap' }}>{fechaHora(e.created_at)}</td>
              <td>{ACCIONES[e.accion] || e.accion}</td>
              <td>{ORIGENES[e.origen] || e.origen}</td>
              <td><pre className="afe-json">{JSON.stringify(e.detalle, null, 2)}</pre></td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function DocumentosModal({ empresa, mes, onCerrar }) {
  const [documentos, setDocumentos] = useState(null);
  const [auditar, setAuditar] = useState(null);

  useEffect(() => {
    getFacturadorDocumentos(empresa.id_company, mes)
      .then(setDocumentos)
      .catch((err) => Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudieron cargar los documentos') }));
  }, [empresa.id_company, mes]);

  return (
    <div className="fe-modal-overlay" onClick={onCerrar}>
      <div className="fe-modal fe-modal--ancho" onClick={(e) => e.stopPropagation()}>
        <h3 className="fe-modal-title">Documentos de {nombreMes(mes)} · {empresa.empresa}</h3>
        {auditar ? (
          <Auditoria idDocumento={auditar} onVolver={() => setAuditar(null)} />
        ) : !documentos ? (
          <p className="fe-loading">Cargando...</p>
        ) : documentos.length === 0 ? (
          <p className="fe-empty">Sin documentos este mes.</p>
        ) : (
          <table className="fe-table">
            <thead><tr><th>Fecha</th><th>Documento</th><th>Receptor</th><th className="fe-num">Total</th><th>Estado</th><th /></tr></thead>
            <tbody>
              {documentos.map((d) => (
                <tr key={d.id}>
                  <td>{fechaCorta(d.fecha_emision)}</td>
                  <td>{nombreDocumento(d)}<div className="fe-muted">{ORIGENES[d.origen] || d.origen}</div></td>
                  <td>{d.razon_social_receptor}</td>
                  <td className="fe-num">{pesos(d.monto_total)}</td>
                  <td><EstadoBadge estado={d.estado} /></td>
                  <td><button className="fe-btn fe-btn--link" onClick={() => setAuditar(d.id)}>Auditoría</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="fe-form-actions">
          <button className="fe-btn fe-btn--secondary" onClick={onCerrar}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Pestaña adicionales ──────────────────────────────────────────────────────

function Adicionales({ mes }) {
  const [reporte, setReporte] = useState(null);

  useEffect(() => {
    setReporte(null);
    getFacturadorAdicionales(mes)
      .then(setReporte)
      .catch((err) => Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudo cargar el reporte') }));
  }, [mes]);

  if (!reporte) return <p className="fe-loading">Cargando reporte...</p>;
  const total = reporte.empresas.reduce((suma, e) => suma + (e.total_adicionales || 0), 0);
  const sinValor = reporte.empresas.some((e) => e.adicionales > 0 && e.valor_adicional === null);

  return (
    <div className="fe-table-container">
      <table className="fe-table">
        <thead>
          <tr><th>Empresa</th><th className="fe-num">Documentos</th><th className="fe-num">Cupo</th><th className="fe-num">Adicionales</th><th className="fe-num">Valor</th><th className="fe-num">Total a cobrar</th></tr>
        </thead>
        <tbody>
          {reporte.empresas.map((e) => (
            <tr key={e.id_company}>
              <td>{e.empresa}<div className="fe-muted">{e.rut}</div></td>
              <td className="fe-num">{e.documentos}</td>
              <td className="fe-num">{e.cupo_mensual}</td>
              <td className="fe-num">{e.adicionales}</td>
              <td className="fe-num">{e.valor_adicional === null ? '—' : pesos(e.valor_adicional)}</td>
              <td className="fe-num">{e.total_adicionales === null ? '—' : pesos(e.total_adicionales)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={5}><strong>Total del mes</strong></td>
            <td className="fe-num"><strong>{pesos(total)}</strong></td>
          </tr>
        </tbody>
      </table>
      {sinValor && <p className="fe-hint" style={{ padding: '0 14px 12px' }}>Hay empresas con adicionales sin valor por adicional: configúralo en Límites.</p>}
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

export default function AdminFacturadorPage() {
  const navigate = useNavigate();
  const [mes, setMes] = useState(hoyChile().slice(0, 7));
  const [tab, setTab] = useState('empresas');
  const [empresas, setEmpresas] = useState(null);
  const [modal, setModal] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const datos = await getFacturadorEmpresas(mes);
      setEmpresas(datos.empresas);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudieron cargar las empresas') });
    }
  }, [mes]);

  useEffect(() => { cargar(); }, [cargar]);

  const activar = async (empresa) => {
    const respuesta = await Swal.fire({
      icon: 'question',
      title: `¿Activar el facturador para ${empresa.empresa}?`,
      text: 'Parte en el ambiente de certificación con los documentos de la fase 1. Después registra la resolución y los límites.',
      showCancelButton: true,
      confirmButtonText: 'Activar',
      cancelButtonText: 'Cancelar',
    });
    if (!respuesta.isConfirmed) return;
    try {
      await activarFacturador(empresa.id_company);
      cargar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: mensajeError(err, 'No se pudo activar') });
    }
  };

  const guardado = () => {
    setModal(null);
    cargar();
  };

  const claseUso = (e) => {
    if (e.tope_alcanzado) return 'afe-fila--tope';
    if (e.porcentaje_cupo !== null && e.porcentaje_cupo >= 100) return 'afe-fila--sobre';
    if (e.porcentaje_cupo !== null && e.porcentaje_cupo >= 80) return 'afe-fila--cerca';
    return '';
  };

  return (
    <div className="admin-facturador-page">
      <header className="admin-header">
        <div className="header-content">
          <div className="header-left">
            <button className="btn-back" onClick={() => navigate('/admin/clientes')}>← Volver</button>
            <h1>Facturador electrónico</h1>
          </div>
        </div>
      </header>

      <div className="fe-page">
        <div className="fe-filtros">
          <FiltroPeriodo
            anio={mes.slice(0, 4)}
            mes={mes.slice(5)}
            onCambio={(nuevoAnio, nuevoMes) => setMes(`${nuevoAnio}-${nuevoMes}`)}
          />
          <div className="fe-opciones" style={{ marginBottom: 0 }}>
            <button className={`fe-tipo-btn ${tab === 'empresas' ? 'fe-tipo-btn--activo' : ''}`} onClick={() => setTab('empresas')}>Empresas</button>
            <button className={`fe-tipo-btn ${tab === 'adicionales' ? 'fe-tipo-btn--activo' : ''}`} onClick={() => setTab('adicionales')}>Adicionales del mes</button>
          </div>
        </div>

        {tab === 'adicionales' ? (
          <Adicionales mes={mes} />
        ) : !empresas ? (
          <p className="fe-loading">Cargando empresas...</p>
        ) : (
          <div className="fe-table-container">
            <table className="fe-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Facturador</th>
                  <th>Certificado</th>
                  <th>Folios</th>
                  <th className="fe-num">Uso del mes</th>
                  <th className="fe-num">Adicionales</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {empresas.map((e) => (
                  <tr key={e.id_company} className={claseUso(e)}>
                    <td>{e.empresa}<div className="fe-muted">{e.rut || 'Sin RUT'}</div></td>
                    <td>
                      {!e.activado ? (
                        <span className="fe-badge fe-badge--gris">No activado</span>
                      ) : (
                        <>
                          <span className={`fe-badge ${e.ambiente === 'produccion' ? 'fe-badge--verde' : 'fe-badge--amarillo'}`}>
                            {e.ambiente === 'produccion' ? 'Producción' : 'Certificación'}
                          </span>
                          <div className="fe-muted">
                            {e.resolucion_fecha ? `Res. ${e.resolucion_numero} del ${fechaCorta(e.resolucion_fecha)}` : 'Sin resolución'}
                          </div>
                        </>
                      )}
                    </td>
                    <td>{e.activado ? (e.certificado_vence ? `Vence ${fechaChile(e.certificado_vence)}` : 'Sin certificado') : '—'}</td>
                    <td>
                      {e.activado && e.folios.length > 0
                        ? e.folios.map((f) => <div key={f.tipo} className="fe-muted">{f.tipo}: {f.disponibles}</div>)
                        : e.activado ? 'Sin folios' : '—'}
                    </td>
                    <td className="fe-num">
                      {e.activado ? (
                        <>
                          {e.usados}{e.cupo_mensual > 0 ? ` / ${e.cupo_mensual}` : ''}
                          {e.tope_mensual !== null && <div className="fe-muted">tope {e.tope_mensual}</div>}
                          {e.tope_alcanzado && <div><span className="fe-badge fe-badge--rojo">Tope alcanzado</span></div>}
                        </>
                      ) : '—'}
                    </td>
                    <td className="fe-num">
                      {e.activado ? (
                        <>
                          {e.adicionales}
                          {e.total_adicionales !== null && e.adicionales > 0 && <div className="fe-muted">{pesos(e.total_adicionales)}</div>}
                        </>
                      ) : '—'}
                    </td>
                    <td>
                      <div className="fe-acciones">
                        {!e.activado ? (
                          <button className="fe-btn fe-btn--primary" onClick={() => activar(e)}>Activar</button>
                        ) : (
                          <>
                            <button className="fe-btn fe-btn--link" onClick={() => setModal({ tipo: 'configurar', empresa: e })}>Configurar</button>
                            <button className="fe-btn fe-btn--link" onClick={() => setModal({ tipo: 'limites', empresa: e })}>Límites</button>
                            <button className="fe-btn fe-btn--link" onClick={() => setModal({ tipo: 'documentos', empresa: e })}>Documentos</button>
                            <button className="fe-btn fe-btn--link" onClick={() => navigate(`/admin/facturador/${e.id_company}/certificacion`)}>Certificación</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {modal?.tipo === 'configurar' && <ConfigurarModal empresa={modal.empresa} onGuardado={guardado} onCerrar={() => setModal(null)} />}
        {modal?.tipo === 'limites' && <LimitesModal empresa={modal.empresa} onGuardado={guardado} onCerrar={() => setModal(null)} />}
        {modal?.tipo === 'documentos' && <DocumentosModal empresa={modal.empresa} mes={mes} onCerrar={() => setModal(null)} />}
      </div>
    </div>
  );
}
